/**
 * Desembrulho do envelope de resposta do backend.
 *
 * O `VersionHateoasInterceptor` global não aninha o payload de forma
 * uniforme:
 *
 *   objeto → `{ version, _links, ...objeto }`   (espalhado)
 *   array  → `{ version, _links, data: [...] }` (sob `data`)
 *
 * A diferença importa porque as respostas paginadas deste módulo já têm
 * um campo `data` próprio: o envelope e o payload colidem na mesma
 * chave. Um desembrulho genérico de "o `data`" devolve o array cru onde
 * a tela espera `{ data, total, limit, offset }` — e a lista aparece
 * vazia mesmo com o backend respondendo certo.
 *
 * Por isso são três funções explícitas em vez de uma esperta: cada
 * chamada declara a forma que recebe, e o módulo fica testável sem
 * axios nem `import.meta.env`.
 */

export interface Paginated<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

type Envelope = Record<string, unknown> & { version?: unknown; _links?: unknown };

/** Recurso único: o corpo é o próprio objeto, ao lado dos campos do envelope. */
export function unwrapObject<T>(body: unknown): T {
  if (!body || typeof body !== 'object' || Array.isArray(body)) return body as T;
  const { version: _version, _links: _hateoas, ...rest } = body as Envelope;
  return rest as T;
}

/** Lista simples: o interceptor a coloca sob `data`. */
export function unwrapArray<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as Envelope | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

/**
 * Página: `data` é o array do payload (não do envelope) e
 * `total`/`limit`/`offset` vêm ao lado dele.
 *
 * Os defaults existem para o caso de a rota responder só o array — a
 * tela continua paginando sobre o que recebeu em vez de mostrar zero.
 */
export function unwrapPaginated<T>(body: unknown): Paginated<T> {
  if (Array.isArray(body)) {
    return { data: body as T[], total: body.length, limit: body.length, offset: 0 };
  }
  const envelope = (body ?? {}) as Envelope;
  const data = Array.isArray(envelope.data) ? (envelope.data as T[]) : [];
  return {
    data,
    total: typeof envelope.total === 'number' ? envelope.total : data.length,
    limit: typeof envelope.limit === 'number' ? envelope.limit : data.length,
    offset: typeof envelope.offset === 'number' ? envelope.offset : 0,
  };
}
